/** @jsxImportSource @emotion/react */
import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Form, InputNumber, Input, Select, message, Space, Typography, Divider, Slider, Row, Col } from 'antd';
import { css } from '@emotion/react';
import commonStyles from '../../styles/commonStyles';
import { skipToken } from '@reduxjs/toolkit/query';
import { useCompleteEventMutation, useGetEventByIdQuery } from '../../api/plantingEventsApi';
import { useGetEventTemplateByIdQuery } from '../../api/eventTemplatesApi';
import { EventQueueItem, Property, PropertyDataType, CompleteEventPayload } from '../../types/plantingEvents';
import { useSave } from '../../contexts/WorldContext';

const { Option } = Select;
const { Text } = Typography;

const styles = {
  quantitySection: css({ marginTop: 16 }),
};

interface CompleteEventModalProps {
  open: boolean;
  event: EventQueueItem | null;
  farmId: string;
  onClose: () => void;
  onSuccess?: () => void;
}

/**
 * Модалка для завершения события из очереди с опциональными измерениями
 */
export const CompleteEventModal: React.FC<CompleteEventModalProps> = ({
  open,
  event,
  farmId,
  onClose,
  onSuccess,
}) => {
  const save = useSave();
  const [form] = Form.useForm();
  const [attributeValues, setAttributeValues] = useState<Record<string, number | string | boolean>>({});
  const [quantityAffected, setQuantityAffected] = useState<number | undefined>(undefined);

  const [completeEvent, { isLoading: completing }] = useCompleteEventMutation();

  // Загружаем полные данные события, включая plantingType и plantingQuantity
  const { data: eventDetail } = useGetEventByIdQuery(
    event?.id && open ? { saveId: save.id, eventId: event.id } : skipToken
  );
  
  // CN-0014: Properties загружаются из шаблона (а не из старых attributes)
  // Для событий без шаблона properties не нужны

  // Загружаем детали шаблона, если событие создано из шаблона
  const { data: templateDetail, isLoading: loadingTemplate } = useGetEventTemplateByIdQuery(
    event?.templateId && open && save?.id ? { id: event.templateId, saveId: save.id } : skipToken
  );



  // Фильтруем атрибуты: показываем ТОЛЬКО если событие создано из шаблона
  const attributes = useMemo(() => {
    // Если есть templateId - ждем загрузки шаблона
    if (event?.templateId) {
      if (loadingTemplate) {
        return []; // Показываем пустой список пока загружается
      }
      if (templateDetail?.properties && templateDetail.properties.length > 0) {
        // Преобразуем properties шаблона в формат Property
        return templateDetail.properties.map((templateProp) => {
          // Маппинг типов данных: decimal → float, number → integer
          let mappedDataType: PropertyDataType;
          switch (templateProp.dataType) {
            case 'number':
              mappedDataType = 'integer';
              break;
            case 'decimal':
              mappedDataType = 'float';
              break;
            case 'text':
            case 'boolean':
              mappedDataType = templateProp.dataType;
              break;
            default:
              mappedDataType = 'text'; // fallback
          }

          return {
            id: templateProp.propertyId,
            code: templateProp.code,
            name: templateProp.name,
            objectType: 'planting' as const,
            dataType: mappedDataType,
            dataTypeCode: mappedDataType === 'integer' ? 0 : mappedDataType === 'text' ? 1 : mappedDataType === 'float' ? 2 : 3,
            unitId: null,
            unit: templateProp.unit, // Добавляем unit из шаблона
            options: templateProp.options?.map(opt => opt.value) ?? [],
            isService: false,
            semanticType: 'measured' as const,
          };
        });
      }
      // Если шаблон загрузился, но атрибутов нет - возвращаем пустой массив
      return [];
    }
    // ИСПРАВЛЕНИЕ CN-0008: Если нет templateId - НЕ показываем атрибуты
    // Событие без шаблона завершается без заполнения атрибутов
    return [];
  }, [event?.templateId, templateDetail, loadingTemplate]);

  // Сбросить форму при открытии/закрытии
  useEffect(() => {
    if (open && event && eventDetail) {
      form.resetFields();
      setAttributeValues({});
      // Устанавливаем значение по умолчанию для групповых посадок
      if (eventDetail.plantingType === 'batch') {
        // По умолчанию - полное выполнение (максимальное значение)
        setQuantityAffected(eventDetail.plantingQuantity);
      } else {
        setQuantityAffected(undefined);
      }
    }
  }, [open, event, eventDetail, form]);

  const handleSubmit = async () => {
    if (!event || !eventDetail) return;

    try {
      // Валидация для групповых посадок (CN-0007)
      if (eventDetail.plantingType === 'batch' && quantityAffected !== undefined) {
        const currentCompleted = eventDetail.quantityAffected || 0;
        const minAllowed = currentCompleted + 1;
        
        if (quantityAffected < minAllowed) {
          message.error(`Минимальное количество: ${minAllowed} (уже выполнено: ${currentCompleted})`);
          return;
        }
        if (quantityAffected > eventDetail.plantingQuantity) {
          message.error(`Максимум ${eventDetail.plantingQuantity} единиц`);
          return;
        }
      }
      
      // Формируем payload (properties в объектном формате {"26": 215})
      const payload: CompleteEventPayload = {};
      
      if (Object.keys(attributeValues).length > 0) {
        payload.properties = attributeValues;
      }
      
      // Добавляем quantity_affected для групповых посадок
      if (eventDetail.plantingType === 'batch' && quantityAffected !== undefined) {
        payload.quantity_affected = quantityAffected;
      }

      const result = await completeEvent({
        saveId: save.id,
        eventId: event.id,
        payload,
      }).unwrap();
      
      // Обработка частичного выполнения для batch plantings
      if (result.partialCompletion) {
        const { quantityAffected, totalQuantity, isFullyCompleted } = result.partialCompletion;
        if (isFullyCompleted) {
          message.success(
            result.updatedProperties > 0
              ? `Задача полностью завершена! Обработано: ${quantityAffected}/${totalQuantity}. Обновлено свойств: ${result.updatedProperties}`
              : `Задача полностью завершена! Обработано: ${quantityAffected}/${totalQuantity}`
          );
        } else {
          message.info(
            result.updatedProperties > 0
              ? `Частично выполнено: ${quantityAffected}/${totalQuantity} растений. Обновлено свойств: ${result.updatedProperties}. Задача остается в очереди.`
              : `Частично выполнено: ${quantityAffected}/${totalQuantity} растений. Задача остается в очереди.`
          );
        }
      } else {
        // Обычное завершение для single plantings
        message.success(
          result.updatedProperties > 0
            ? `Событие завершено! Обновлено свойств: ${result.updatedProperties}`
            : 'Событие успешно завершено'
        );
      }
      
      onSuccess?.();
      onClose();
    } catch (err: any) {
      console.error('Ошибка завершения события:', err);
      message.error(err?.data?.message || 'Не удалось завершить событие');
    }
  };

  const handleAttributeChange = (attrId: number, value: number | string | boolean | null) => {
    if (value === null || value === undefined || value === '') {
      // Удаляем атрибут из объекта
      const newValues = { ...attributeValues };
      delete newValues[String(attrId)];
      setAttributeValues(newValues);
    } else {
      // Добавляем/обновляем атрибут
      setAttributeValues({
        ...attributeValues,
        [String(attrId)]: value,
      });
    }
  };

  const renderAttributeInput = (attr: Property) => {
    const value = attributeValues[String(attr.id)];

    switch (attr.dataType) {
      case 'integer':
        return (
          <InputNumber
            css={commonStyles.fullWidth}
            placeholder={`Введите ${attr.name.toLowerCase()}`}
            value={value as number}
            onChange={(val) => handleAttributeChange(attr.id, val)}
            addonAfter={attr.unit || undefined}
          />
        );

      case 'float':
        return (
          <InputNumber
            css={commonStyles.fullWidth}
            placeholder={`Введите ${attr.name.toLowerCase()}`}
            value={value as number}
            onChange={(val) => handleAttributeChange(attr.id, val)}
            step={0.1}
            precision={2}
            addonAfter={attr.unit || undefined}
          />
        );

      case 'text':
        if (attr.options && attr.options.length > 0) {
          // Если есть опции - показываем Select
          return (
            <Select
              css={commonStyles.fullWidth}
              placeholder={`Выберите ${attr.name.toLowerCase()}`}
              value={value as string}
              onChange={(val) => handleAttributeChange(attr.id, val)}
              allowClear
            >
              {attr.options.map((opt) => (
                <Option key={opt} value={opt}>
                  {opt}
                </Option>
              ))}
            </Select>
          );
        }
        // Иначе - обычный Input
        return (
          <Input
            placeholder={`Введите ${attr.name.toLowerCase()}`}
            value={value as string}
            onChange={(e) => handleAttributeChange(attr.id, e.target.value)}
          />
        );

      case 'boolean':
        return (
          <Select
            css={commonStyles.fullWidth}
            placeholder="Выберите значение"
            value={value as boolean}
            onChange={(val) => handleAttributeChange(attr.id, val)}
            allowClear
          >
            <Option value={true}>Да</Option>
            <Option value={false}>Нет</Option>
          </Select>
        );

      default:
        return null;
    }
  };

  if (!event) return null;

  return (
    <Modal
      title={`Завершить событие #${event.id}`}
      open={open}
      onCancel={onClose}
      onOk={handleSubmit}
      okText="Выполнить"
      cancelText="Отмена"
      width={600}
      confirmLoading={completing}
    >
      <Space direction="vertical" css={commonStyles.fullWidth} size="large">
        {/* Информация о событии */}
        <div>
          <Text strong>Культура:</Text> {event.cropName} <Text type="secondary">({event.cropLatinName})</Text>
          <br />
          <Text strong>Тип:</Text> {event.type}
          <br />
          <Text strong>Плановая дата:</Text> {event.plannedDate}
          {event.notes && (
            <>
              <br />
              <Text strong>Примечания:</Text> <Text type="secondary">{event.notes}</Text>
            </>
          )}
        </div>

        {/* Количество для групповых посадок (CN-0007) */}
        {eventDetail && eventDetail.plantingType === 'batch' && (() => {
          const currentCompleted = eventDetail.quantityAffected || 0;
          const totalQuantity = eventDetail.plantingQuantity;
          const minValue = currentCompleted + 1;
          const maxValue = totalQuantity;
          
          return (
            <>
              <Divider css={css({ margin: '8px 0' })} />
              <div>
                <Text strong>Количество затронутых растений</Text>
                <br />
                <Text type="secondary" css={commonStyles.smallText}>
                  Всего в партии: {totalQuantity}, уже выполнено: {currentCompleted}
                </Text>
                <Row gutter={16} css={styles.quantitySection}>
                  <Col span={16}>
                    <Slider
                      min={minValue}
                      max={maxValue}
                      step={1}
                      value={quantityAffected}
                      onChange={(val) => setQuantityAffected(val)}
                      marks={{
                        [minValue]: `${minValue}`,
                        [maxValue]: `${maxValue}`,
                      }}
                      tooltip={{
                        formatter: (value) => `${value} шт.`,
                      }}
                    />
                  </Col>
                  <Col span={8}>
                    <InputNumber
                      min={minValue}
                      max={maxValue}
                      step={1}
                      value={quantityAffected}
                      onChange={(val) => setQuantityAffected(val ?? minValue)}
                      css={commonStyles.fullWidth}
                      addonAfter="шт."
                    />
                  </Col>
                </Row>
              </div>
            </>
          );
        })()}

        <Divider css={css({ margin: '8px 0' })} />

        {/* Опциональные измерения */}
        <div>
          <Text strong>Измерения (опционально)</Text>
          <br />
          <Text type="secondary" css={commonStyles.smallText}>
            Вы можете указать измеренные параметры или оставить пустыми
          </Text>
        </div>

        {loadingTemplate ? (
          <Text type="secondary">Загрузка атрибутов шаблона...</Text>
        ) : attributes.length === 0 ? (
          <Text type="secondary">
            {event?.templateId 
              ? 'Нет атрибутов для заполнения' 
              : 'Событие без шаблона — атрибуты не требуются'}
          </Text>
        ) : (
          <Form form={form} layout="vertical">
            {attributes.map((attr) => (
                <Form.Item
                  key={attr.id}
                  label={attr.name}
                  help={attr.code}
                >
                  {renderAttributeInput(attr)}
                </Form.Item>
              ))}
          </Form>
        )}
      </Space>
    </Modal>
  );
};
